import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
} from "../api/users";
import {
  getDepartments,
  createDepartment,
  deleteDepartment,
} from "../api/departments";
import {
  getItemCategories,
  createItemCategory,
  deleteItemCategory,
} from "../api/itemCategories";

import "../styles/AdminPage.css";
import { deleteStalePendingRequests, getStalePendingRequests } from "../api/dashboard";

const ACCESS_OPTIONS = [
  ["request", "Create & view requests"], ["approve", "Approve requests"],
  ["finance", "Finance workspace"], ["stores", "Stores workspace"], ["admin", "Administration"],
];

const AdminPage = () => {
  const [activeTab, setActiveTab] = useState("users");

  // Users state
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    role: "requester",
    access_rights: ["request"],
    approver_level: "",
    department_id: "",
    password: "",
    first_name: "",
    last_name: "",
  });
  const [editingUser, setEditingUser] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Departments state
  const [departments, setDepartments] = useState([]);
  const [newDeptName, setNewDeptName] = useState("");
  const [loadingDepts, setLoadingDepts] = useState(false);

  // Categories state
  const [categories, setCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryLeadTime, setNewCategoryLeadTime] = useState(1);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [staleDays, setStaleDays] = useState(90);
  const [staleData, setStaleData] = useState({ count: 0, items: [] });
  const [selectedStale, setSelectedStale] = useState([]);
  const [loadingStale, setLoadingStale] = useState(false);

  // Fetch users
  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (err) {
      console.error(err);
      Swal.fire("❌ Error", "Failed to fetch users.", "error");
    } finally {
      setLoadingUsers(false);
    }
  };

  // Fetch departments
  const fetchDepartments = async () => {
    setLoadingDepts(true);
    try {
      const data = await getDepartments();
      setDepartments(data);
    } catch (err) {
      console.error(err);
      Swal.fire("❌ Error", "Failed to fetch departments.", "error");
    } finally {
      setLoadingDepts(false);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const data = await getItemCategories();
      setCategories(data);
    } catch (err) {
      console.error(err);
      Swal.fire("❌ Error", "Failed to fetch categories.", "error");
    } finally {
      setLoadingCategories(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchUsers();
    fetchDepartments();
    fetchCategories();
  }, []);

  // Handle user input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "role" && value !== "approver") {
      setFormData({
        ...formData,
        role: value,
        approver_level: "",
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const toggleAccessRight = (right) => {
    setFormData((current) => ({
      ...current,
      access_rights: current.access_rights.includes(right)
        ? current.access_rights.filter((item) => item !== right)
        : [...current.access_rights, right],
    }));
  };

  // Handle user form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        approver_level:
          formData.access_rights.includes("approve") ? Number(formData.approver_level) : null,
      };
      if (editingUser) {
        await updateUser(editingUser.id, payload);
        Swal.fire({
          icon: "success",
          title: "User Updated 🎉",
          text: `${formData.username} has been updated successfully.`,
          confirmButtonColor: "#a92f6f",
        });
      } else {
        await createUser(payload);
        Swal.fire({
          icon: "success",
          title: "User Created 🙌",
          text: `${formData.username} has been added successfully.`,
          confirmButtonColor: "#a92f6f",
        });
      }
      setFormData({
        username: "",
        email: "",
        role: "requester",
        access_rights: ["request"],
        approver_level: "",
        department_id: "",
        password: "",
        first_name: "",
        last_name: "",
      });
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      console.error(err);
      Swal.fire("❌ Error", "Failed to save user.", "error");
    }
  };

  // Handle edit user
  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      role: user.role,
      access_rights: user.access_rights || [],
      approver_level: user.approver_level || "",
      department_id: user.department?.id || "",
      password: "",
      first_name: user.first_name || "",
      last_name: user.last_name || "",
    });
    Swal.fire({
      icon: "info",
      title: "Edit Mode ✏️",
      text: `You are editing ${user.username}.`,
      confirmButtonColor: "#a92f6f",
    });
  };

  const fetchStale = async () => {
    setLoadingStale(true);
    try {
      setStaleData(await getStalePendingRequests(staleDays));
      setSelectedStale([]);
    } catch (err) {
      Swal.fire("Error", err.response?.data?.detail || "Could not load pending requests.", "error");
    } finally { setLoadingStale(false); }
  };

  const removeSelectedStale = async () => {
    if (!selectedStale.length) return;
    const result = await Swal.fire({
      title: `Delete ${selectedStale.length} pending request(s)?`,
      text: "This permanently removes the requests and their approval records. This cannot be undone.",
      icon: "warning", showCancelButton: true, confirmButtonText: "Delete permanently",
      confirmButtonColor: "#a92f6f",
    });
    if (!result.isConfirmed) return;
    try {
      const response = await deleteStalePendingRequests(selectedStale, staleDays);
      Swal.fire("Removed", `${response.deleted} eligible request(s) were permanently deleted.`, "success");
      fetchStale();
    } catch (err) {
      Swal.fire("Error", err.response?.data?.detail || "Deletion failed.", "error");
    }
  };

  // Handle delete user
  const handleDeleteUser = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This user will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#a92f6f",
      cancelButtonColor: "#a9ae50",
      confirmButtonText: "Yes, delete it!",
    });

    if (confirm.isConfirmed) {
      try {
        await deleteUser(id);
        Swal.fire("Deleted!", "User has been removed 🗑️", "success");
        fetchUsers();
      } catch (err) {
        console.error(err);
        Swal.fire("❌ Error", "Failed to delete user.", "error");
      }
    }
  };

  // Handle create department
  const handleCreateDepartment = async () => {
    if (!newDeptName.trim()) {
      return Swal.fire("⚠️ Warning", "Enter department name", "warning");
    }
    try {
      await createDepartment({ name: newDeptName });
      setNewDeptName("");
      Swal.fire({
        icon: "success",
        title: "Department Created 🏢",
        text: "New department added successfully!",
        confirmButtonColor: "#a92f6f",
      });
      fetchDepartments();
    } catch (err) {
      console.error(err);
      Swal.fire("❌ Error", "Failed to create department.", "error");
    }
  };

  // Handle delete department
  const handleDeleteDepartment = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This department will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#a92f6f",
      cancelButtonColor: "#a9ae50",
      confirmButtonText: "Yes, delete it!",
    });

    if (confirm.isConfirmed) {
      try {
        await deleteDepartment(id);
        Swal.fire("Deleted!", "Department has been removed 🗑️", "success");
        fetchDepartments();
      } catch (err) {
        console.error(err);
        Swal.fire("❌ Error", "Failed to delete department.", "error");
      }
    }
  };

  // Handle create category
  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      return Swal.fire("⚠️ Warning", "Enter category name", "warning");
    }
    try {
      await createItemCategory({
        name: newCategoryName,
        lead_time_days: newCategoryLeadTime,
      });
      setNewCategoryName("");
      setNewCategoryLeadTime(1);
      Swal.fire({
        icon: "success",
        title: "Category Created 📦",
        text: "New item category added successfully!",
        confirmButtonColor: "#a92f6f",
      });
      fetchCategories();
    } catch (err) {
      console.error(err);
      Swal.fire("❌ Error", "Failed to create category.", "error");
    }
  };

  // Handle delete category
  const handleDeleteCategory = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This category will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#a92f6f",
      cancelButtonColor: "#a9ae50",
      confirmButtonText: "Yes, delete it!",
    });

    if (confirm.isConfirmed) {
      try {
        await deleteItemCategory(id);
        Swal.fire("Deleted!", "Category has been removed 🗑️", "success");
        fetchCategories();
      } catch (err) {
        console.error(err);
        Swal.fire("❌ Error", "Failed to delete category.", "error");
      }
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <p className="admin-subtitle">Manage users, departments, and item categories</p>
      </div>

      {/* Navigation Tabs */}
      <div className="tabs">
        <button
          className={activeTab === "users" ? "active" : ""}
          onClick={() => setActiveTab("users")}
        >
          <span className="tab-icon">👤</span>
          <span className="tab-label">Users</span>
        </button>
        <button className={activeTab === "pending-cleanup" ? "active" : ""} onClick={() => { setActiveTab("pending-cleanup"); fetchStale(); }}>
          <span className="tab-icon">⏱️</span><span className="tab-label">Pending Cleanup</span>
        </button>
        <button
          className={activeTab === "departments" ? "active" : ""}
          onClick={() => setActiveTab("departments")}
        >
          <span className="tab-icon">🏢</span>
          <span className="tab-label">Departments</span>
        </button>
        <button
          className={activeTab === "categories" ? "active" : ""}
          onClick={() => setActiveTab("categories")}
        >
          <span className="tab-icon">📦</span>
          <span className="tab-label">Categories</span>
        </button>
      </div>

      {/* Users Management Section */}
      {activeTab === "users" && (
        <div className="tab-content">
          <div className="form-section card">
            <div className="card-header">
              <h2>{editingUser ? "✏️ Edit User" : "➕ Add New User"}</h2>
            </div>
            <form onSubmit={handleSubmit} className="user-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="username">Username</label>
                  <input
                    id="username"
                    type="text"
                    name="username"
                    placeholder="Enter username"
                    value={formData.username}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    id="password"
                    type="password"
                    name="password"
                    placeholder={editingUser ? "Leave blank to keep current" : "Enter password"}
                    value={formData.password}
                    onChange={handleChange}
                    required={!editingUser}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="first_name">First Name</label>
                  <input
                    id="first_name"
                    type="text"
                    name="first_name"
                    placeholder="Enter first name"
                    value={formData.first_name}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="last_name">Last Name</label>
                  <input
                    id="last_name"
                    type="text"
                    name="last_name"
                    placeholder="Enter last name"
                    value={formData.last_name}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    placeholder="Enter email address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="role">Primary landing page</label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                  >
                    <option value="requester">Requester</option>
                    <option value="approver">Approver</option>
                    <option value="finance">Finance</option>
                    <option value="stores">Stores</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>

              <div className="access-rights-group">
                <label>Access rights</label>
                <div className="access-rights-grid">
                  {ACCESS_OPTIONS.map(([right, label]) => <label className="access-check" key={right}>
                    <input type="checkbox" checked={formData.access_rights.includes(right)} onChange={() => toggleAccessRight(right)} />
                    <span>{label}</span>
                  </label>)}
                </div>
              </div>

              {formData.access_rights.includes("approve") && (
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="approver_level">Approver Level</label>
                    <select
                      id="approver_level"
                      name="approver_level"
                      value={formData.approver_level}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Approver Level</option>
                      <option value="1">1st Approver</option>
                      <option value="2">2nd Approver</option>
                    </select>
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="department_id">Department</label>
                  <select
                    id="department_id"
                    name="department_id"
                    value={formData.department_id}
                    onChange={handleChange}
                  >
                    <option value="">Select Department</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  {editingUser ? "Update User" : "Create User"}
                </button>
                {editingUser && (
                  <button type="button" className="btn-secondary" onClick={() => setEditingUser(null)}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Users Table */}
          <div className="table-section card">
            <div className="card-header">
              <h2>📋 Users List</h2>
              <span className="badge">{users.length} total</span>
            </div>
            {loadingUsers ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading users...</p>
              </div>
            ) : users.length === 0 ? (
              <div className="empty-state">
                <p>No users found.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Access rights</th>
                      <th>Approver Level</th>
                      <th>Department</th>
                      <th className="actions-header">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td className="user-name">{user.username}</td>
                        <td>{user.email}</td>
                        <td>
                          <div className="rights-list">{(user.access_rights || [user.role]).map((right) => <span key={right} className={`role-badge role-${right}`}>{right}</span>)}</div>
                        </td>
                        <td>
                          {(user.access_rights || []).includes("approve") || user.role === "approver"
                            ? user.approver_level
                            : "-"}
                        </td>
                        <td>{user.department?.name || "-"}</td>
                        <td className="actions-cell">
                          <button className="btn-icon btn-edit" onClick={() => handleEdit(user)} title="Edit">
                            ✏️
                          </button>
                          <button className="btn-icon btn-delete" onClick={() => handleDeleteUser(user.id)} title="Delete">
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "pending-cleanup" && (
        <div className="tab-content">
          <div className="card stale-review">
            <div className="card-header"><div><h2>Pending requests review</h2><p>Find requests that have received no approval and are older than the selected period.</p></div><span className="badge">{staleData.count} eligible</span></div>
            <div className="stale-toolbar">
              <label>Pending for at least
                <input type="number" min="1" max="3650" list="pending-periods" value={staleDays} onChange={(e) => setStaleDays(Number(e.target.value))} />
                <datalist id="pending-periods"><option value="30" /><option value="90" /><option value="180" /><option value="365" /><option value="730" /></datalist>
              </label>
              <button className="btn-secondary" onClick={fetchStale} disabled={loadingStale}>{loadingStale ? "Loading…" : "Apply period"}</button>
              <button className="btn-primary danger-action" onClick={removeSelectedStale} disabled={!selectedStale.length}>Delete selected ({selectedStale.length})</button>
            </div>
            <div className="table-responsive"><table className="data-table"><thead><tr>
              <th><input type="checkbox" aria-label="Select all" checked={staleData.items.length > 0 && selectedStale.length === staleData.items.length} onChange={(e) => setSelectedStale(e.target.checked ? staleData.items.map((item) => item.id) : [])} /></th>
              <th>Item</th><th>Requester</th><th>Lot</th><th>Submitted</th><th>Age</th>
            </tr></thead><tbody>{staleData.items.map((item) => {
              const age = Math.floor((Date.now() - new Date(item.created_at).getTime()) / 86400000);
              return <tr key={item.id}><td><input type="checkbox" checked={selectedStale.includes(item.id)} onChange={() => setSelectedStale((current) => current.includes(item.id) ? current.filter((id) => id !== item.id) : [...current, item.id])} /></td><td>{item.item}</td><td>{item.requester_name}</td><td>{item.lot_full_number || "—"}</td><td>{new Date(item.created_at).toLocaleDateString()}</td><td><strong>{age} days</strong></td></tr>;
            })}</tbody></table></div>
            {!loadingStale && !staleData.items.length && <div className="empty-state"><p>No pending requests exceed this period.</p></div>}
          </div>
        </div>
      )}

      {/* Departments Management Section */}
      {activeTab === "departments" && (
        <div className="tab-content">
          <div className="form-section card">
            <div className="card-header">
              <h2>➕ Add Department</h2>
            </div>
            <div className="inline-form">
              <div className="form-group flex-grow">
                <label htmlFor="dept-name">Department Name</label>
                <input
                  id="dept-name"
                  type="text"
                  placeholder="Enter department name"
                  value={newDeptName}
                  onChange={(e) => setNewDeptName(e.target.value)}
                />
              </div>
              <button className="btn-primary" onClick={handleCreateDepartment}>
                Add Department
              </button>
            </div>
          </div>

          <div className="table-section card">
            <div className="card-header">
              <h2>🏢 Departments List</h2>
              <span className="badge">{departments.length} total</span>
            </div>
            {loadingDepts ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading departments...</p>
              </div>
            ) : departments.length === 0 ? (
              <div className="empty-state">
                <p>No departments yet.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th className="actions-header">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {departments.map((dept) => (
                      <tr key={dept.id}>
                        <td className="id-cell">#{dept.id}</td>
                        <td className="dept-name">{dept.name}</td>
                        <td className="actions-cell">
                          <button
                            className="btn-icon btn-delete"
                            onClick={() => handleDeleteDepartment(dept.id)}
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Categories Management Section */}
      {activeTab === "categories" && (
        <div className="tab-content">
          <div className="form-section card">
            <div className="card-header">
              <h2>➕ Add Item Category</h2>
            </div>
            <div className="inline-form">
              <div className="form-group flex-grow">
                <label htmlFor="cat-name">Category Name</label>
                <input
                  id="cat-name"
                  type="text"
                  placeholder="Enter category name"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="lead-time">Lead Time (working days)</label>
                <input
                  id="lead-time"
                  type="number"
                  placeholder="Lead Time (working days)"
                  value={newCategoryLeadTime}
                  onChange={(e) =>
                    setNewCategoryLeadTime(Number(e.target.value))
                  }
                  min={1}
                />
              </div>
              <button className="btn-primary" onClick={handleCreateCategory}>
                Add Category
              </button>
            </div>
          </div>

          <div className="table-section card">
            <div className="card-header">
              <h2>📋 Categories List</h2>
              <span className="badge">{categories.length} total</span>
            </div>
            {loadingCategories ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading categories...</p>
              </div>
            ) : categories.length === 0 ? (
              <div className="empty-state">
                <p>No categories yet.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Lead Time (working days)</th>
                      <th>Created By</th>
                      <th className="actions-header">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((cat) => (
                      <tr key={cat.id}>
                        <td className="id-cell">#{cat.id}</td>
                        <td className="cat-name">{cat.name}</td>
                        <td>
                          <span className="lead-time-badge">
                            {cat.lead_time_days} working days
                          </span>
                        </td>
                        <td>{cat.created_by}</td>
                        <td className="actions-cell">
                          <button
                            className="btn-icon btn-delete"
                            onClick={() => handleDeleteCategory(cat.id)}
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
